// Implements: F-UIX-019
/**
 * "Topes" (UIX.8, USG.21; UX-11 `/plataforma/topes`): every organization from
 * `GET /api/organizations` (the same cached read that names the totals) in a table, so an
 * organization with no usage yet can be given a ceiling too. "Editar topes" opens that
 * organization's ceiling in a dialog, saved with its own `PUT` (there is no batch endpoint).
 *
 * Nothing is read for a ceiling until its dialog opens (no ceilings listing: one read per
 * organization would be needed to show them in the table, a backend gap). The dialog is modal and
 * unmounts the editor when it closes, so every opening starts from that organization's stored
 * ceiling and a draft never follows the admin to another one (checklist 14); the focus goes back
 * to the row's button.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Badge } from "../../../shared/ui/atoms/Badge";
import { Button } from "../../../shared/ui/atoms/Button";
import { Card } from "../../../shared/ui/atoms/Card";
import { Skeleton } from "../../../shared/ui/atoms/Skeleton";
import { dayText } from "../../../shared/ui/dayText";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { Dialog } from "../../../shared/ui/organisms/Dialog";
import { PLATFORM_LOCALE } from "../../../types/locale";
import type { OrganizationSummaryView } from "../../../types/reports";
import styles from "../components/Ceilings.module.css";
import { useOrganizationList } from "../hooks/usePlatformTotals";
import { CeilingEditorContainer } from "./CeilingEditorContainer";

const COLLATOR = new Intl.Collator(PLATFORM_LOCALE);

/** Ordered by name (Spanish collation), ties by id, as the totals order their rows. */
function byName(organizations: readonly OrganizationSummaryView[]): OrganizationSummaryView[] {
  return [...organizations].sort((a, b) => COLLATOR.compare(a.name, b.name) || a.id.localeCompare(b.id, PLATFORM_LOCALE));
}

export function CeilingsContainer(): ReactElement {
  const { t } = useTranslation();
  const organizations = useOrganizationList();
  const [chosen, setChosen] = useState<OrganizationSummaryView | null>(null);

  if (organizations.isError) {
    return (
      <ErrorState
        error={organizations.error}
        onRetry={() => {
          void organizations.refetch();
        }}
      />
    );
  }
  if (organizations.data === undefined) {
    return (
      <div role="status" aria-busy="true" aria-label={t("admin.ceilings.listLoading")}>
        <Card padded>
          <div className={styles.skeleton}>
            {[0, 1, 2, 3, 4].map((row) => (
              <Skeleton key={row} />
            ))}
          </div>
        </Card>
      </div>
    );
  }
  if (organizations.data.organizations.length === 0) {
    return <EmptyState title={t("admin.ceilings.noOrganizations")} body={t("admin.ceilings.intro")} icon="building" />;
  }

  return (
    <>
      <Card>
        <div className={styles.scroll}>
          <table className={styles.table} aria-label={t("admin.ceilings.tableLabel")}>
            <thead>
              <tr>
                <th scope="col">{t("admin.ceilings.organization")}</th>
                <th scope="col">{t("admin.ceilings.state")}</th>
                <th scope="col">{t("admin.ceilings.created")}</th>
                <th scope="col">
                  <span className={styles.srOnly}>{t("admin.ceilings.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {byName(organizations.data.organizations).map((organization) => (
                <tr key={organization.id}>
                  <th scope="row">{organization.name}</th>
                  <td>
                    {organization.status === "active" ? (
                      <Badge tone="success">{t("admin.ceilings.status.active")}</Badge>
                    ) : (
                      <Badge tone="neutral">{organization.status}</Badge>
                    )}
                  </td>
                  <td className={styles.muted}>{dayText(organization.created_at)}</td>
                  <td className={styles.action}>
                    <Button
                      variant="ghost"
                      size="sm"
                      hasPopup="dialog"
                      onClick={() => {
                        setChosen(organization);
                      }}
                    >
                      {t("admin.ceilings.edit")}{" "}
                      <span className={styles.srOnly}>{t("admin.ceilings.editOf", { name: organization.name })}</span>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Dialog
        open={chosen !== null}
        title={chosen === null ? "" : t("admin.ceilings.dialogTitle", { name: chosen.name })}
        onClose={() => {
          setChosen(null);
        }}
      >
        {chosen === null ? null : <CeilingEditorContainer organizationId={chosen.id} />}
      </Dialog>
    </>
  );
}
